// Bloque F de 018A — Técnica anestésica (Fase 1B, Tanda 13, ver PLAN.md; Ola 2 M5).
// El formulario oficial imprime las 3 categorías (General, Regional,
// Sedo-analgesia) una al lado de la otra, sin casilla excluyente: en la práctica
// se combinan (p. ej. general + bloqueo regional para analgesia posoperatoria).
// Por eso son casillas COMBINABLES (`categorias`) y se serializa cada categoría
// marcada; el estado de una categoría desmarcada se conserva en memoria (un
// clic errado no borra lo tecleado) pero NUNCA se guarda.
//
// Dentro de Regional, el papel también imprime "Troncular" y "Neuroaxial" a la
// vez, sin elegir: son casillas combinables (`troncular`/`neuroaxial`), con el
// mismo criterio (solo lo marcado se serializa).
//
// Sedo-analgesia: el papel trae un recuadro de renglones libres (~10) para el
// nombre de la sedo-analgesia utilizada (instructivo: "registrar el nombre de la
// sedo-analgesia utilizada") + la escala de Ramsay impresa 1-6. Se capturan los
// fármacos como filas repetibles (tope `TOPE_FARMACOS_SEDOANALGESIA`) y Ramsay 1-6.
//
// Nombre "Datos" (no `tecnicaAnestesica.ts`) porque en Windows el filesystem
// no distingue mayúsculas: colisionaba con `TecnicaAnestesica.tsx`.
import { textoSiNo, type SiNoValor } from './camposSimplesDatos';
import type { Par } from './index';

export type CategoriaTecnicaAnestesica = 'general' | 'regional' | 'sedoanalgesia';

export type SistemaGeneral = 'abierto' | 'semicerrado' | 'cerrado';
export type AparatoGeneral = 'circular' | 'unidireccional';
export type ManejoViaAerea = 'mascarilla-facial' | 'mascarilla-supraglotica' | 'traqueotomo';
export type ViaIntubacion = 'nasal' | 'oral' | 'submentoneana';
export type VisionIntubacion = 'directa' | 'ciegas';
export type TipoTuboGeneral = 'convencional' | 'preformado-oral' | 'preformado-nasal' | 'reforzado' | 'doble-lumen';
export type Cormack = 'I' | 'II' | 'III' | 'IV';
export type InduccionGeneral = 'inhalatoria' | 'intravenosa';
export type MantenimientoGeneral = 'inhalatoria' | 'intravenosa' | 'balanceada';

export const SISTEMA_OPCIONES: readonly SistemaGeneral[] = ['abierto', 'semicerrado', 'cerrado'];
export const APARATO_OPCIONES: readonly AparatoGeneral[] = ['circular', 'unidireccional'];
export const MANEJO_VIA_AEREA_OPCIONES: readonly ManejoViaAerea[] = ['mascarilla-facial', 'mascarilla-supraglotica', 'traqueotomo'];
export const VIA_INTUBACION_OPCIONES: readonly ViaIntubacion[] = ['nasal', 'oral', 'submentoneana'];
export const VISION_INTUBACION_OPCIONES: readonly VisionIntubacion[] = ['directa', 'ciegas'];
export const TIPO_TUBO_OPCIONES: readonly TipoTuboGeneral[] = ['convencional', 'preformado-oral', 'preformado-nasal', 'reforzado', 'doble-lumen'];
export const CORMACK_OPCIONES: readonly Cormack[] = ['I', 'II', 'III', 'IV'];
export const INDUCCION_OPCIONES: readonly InduccionGeneral[] = ['inhalatoria', 'intravenosa'];
export const MANTENIMIENTO_OPCIONES: readonly MantenimientoGeneral[] = ['inhalatoria', 'intravenosa', 'balanceada'];

export interface EstadoTecnicaGeneral {
  readonly sistema: SistemaGeneral | '';
  readonly aparato: AparatoGeneral | '';
  readonly manejoViaAerea: ManejoViaAerea | '';
  readonly viaIntubacion: ViaIntubacion | '';
  readonly visionIntubacion: VisionIntubacion | '';
  readonly tipoTubo: TipoTuboGeneral | '';
  readonly diametroTubo: string;
  /** SI/NO impreso en el papel (018A, F): tri-estado. */
  readonly balon: SiNoValor;
  readonly taponamiento: SiNoValor;
  readonly equipoIntubacion: string;
  readonly cormack: Cormack | '';
  readonly intentosCormack: string;
  readonly induccion: InduccionGeneral | '';
  readonly mantenimiento: MantenimientoGeneral | '';
}

export function estadoInicialGeneral(): EstadoTecnicaGeneral {
  return {
    sistema: '',
    aparato: '',
    manejoViaAerea: '',
    viaIntubacion: '',
    visionIntubacion: '',
    tipoTubo: '',
    diametroTubo: '',
    balon: '',
    taponamiento: '',
    equipoIntubacion: '',
    cormack: '',
    intentosCormack: '',
    induccion: '',
    mantenimiento: '',
  };
}

export type LocalRegional = 'asistida' | 'intravenosa';
export type SubtipoRegional = 'troncular' | 'neuroaxial';
export type AbordajeNeuroaxial = 'raquidea' | 'epidural' | 'caudal';
export type AccesoNeuroaxial = 'medial' | 'lateral';

export const LOCAL_OPCIONES: readonly LocalRegional[] = ['asistida', 'intravenosa'];
export const ABORDAJE_NEUROAXIAL_OPCIONES: readonly AbordajeNeuroaxial[] = ['raquidea', 'epidural', 'caudal'];
export const ACCESO_NEUROAXIAL_OPCIONES: readonly AccesoNeuroaxial[] = ['medial', 'lateral'];

export interface EstadoTecnicaRegional {
  readonly asepsiaCon: string;
  readonly habonCon: string;
  readonly local: LocalRegional | '';
  readonly troncular: boolean;
  readonly neuroaxial: boolean;
  readonly bloqueoNervioPlexo: string;
  readonly intentosTroncular: string;
  readonly anestesicoLocalTroncular: string;
  readonly coadyuvanteTroncular: string;
  readonly tipoAgujaTroncular: string;
  readonly equipoTroncular: string;
  /** En el papel raquídea, epidural y caudal tienen cada una su casilla: se pueden marcar varias. */
  readonly abordajesNeuroaxial: readonly AbordajeNeuroaxial[];
  readonly cateter: boolean;
  readonly tipoAgujaNeuroaxial: string;
  readonly numeroAgujaNeuroaxial: string;
  readonly intentosNeuroaxial: string;
  readonly borbotaje: SiNoValor;
  /** En el papel "Medial" y "Lateral" tienen cada uno su casilla: se pueden marcar ambos. */
  readonly accesosNeuroaxial: readonly AccesoNeuroaxial[];
  readonly sitioPuncion: string;
  readonly dermatoma: string;
  readonly posicionNeuroaxial: string;
}

type CamposTroncular = Pick<EstadoTecnicaRegional, 'bloqueoNervioPlexo' | 'intentosTroncular' | 'anestesicoLocalTroncular' | 'coadyuvanteTroncular' | 'tipoAgujaTroncular' | 'equipoTroncular'>;
type CamposNeuroaxial = Pick<EstadoTecnicaRegional, 'abordajesNeuroaxial' | 'cateter' | 'tipoAgujaNeuroaxial' | 'numeroAgujaNeuroaxial' | 'intentosNeuroaxial' | 'borbotaje' | 'accesosNeuroaxial' | 'sitioPuncion' | 'dermatoma' | 'posicionNeuroaxial'>;

/** Estado vacío de los campos de troncular. */
export function camposTroncularVacios(): CamposTroncular {
  return { bloqueoNervioPlexo: '', intentosTroncular: '', anestesicoLocalTroncular: '', coadyuvanteTroncular: '', tipoAgujaTroncular: '', equipoTroncular: '' };
}

/** Estado vacío de los campos de neuroaxial. */
export function camposNeuroaxialVacios(): CamposNeuroaxial {
  return { abordajesNeuroaxial: [], cateter: false, tipoAgujaNeuroaxial: '', numeroAgujaNeuroaxial: '', intentosNeuroaxial: '', borbotaje: '', accesosNeuroaxial: [], sitioPuncion: '', dermatoma: '', posicionNeuroaxial: '' };
}

export function estadoInicialRegional(): EstadoTecnicaRegional {
  return { asepsiaCon: '', habonCon: '', local: '', troncular: false, neuroaxial: false, ...camposTroncularVacios(), ...camposNeuroaxialVacios() };
}

/** Escala de Ramsay impresa en el papel de 018A: 1 a 6 (la de 019 también trae el 0). */
export const RAMSAY_NIVELES: readonly string[] = ['1', '2', '3', '4', '5', '6'];

/** Renglones libres de "Sedo-analgesia" en el papel (~10). */
export const TOPE_FARMACOS_SEDOANALGESIA = 10;

export interface FilaFarmacoSedoanalgesia {
  readonly id: string;
  readonly texto: string;
}

export function filaFarmacoSedoanalgesiaVacia(): FilaFarmacoSedoanalgesia {
  return { id: crypto.randomUUID(), texto: '' };
}

export interface EstadoTecnicaSedoanalgesia {
  readonly farmacos: readonly FilaFarmacoSedoanalgesia[];
  readonly ramsay: string;
}

export function estadoInicialSedoanalgesia(): EstadoTecnicaSedoanalgesia {
  return { farmacos: [], ramsay: '' };
}

export type CategoriasTecnicaAnestesica = Readonly<Record<CategoriaTecnicaAnestesica, boolean>>;

export const CATEGORIAS_TECNICA: readonly CategoriaTecnicaAnestesica[] = ['general', 'regional', 'sedoanalgesia'];

export interface EstadoTecnicaAnestesica {
  /** Casillas combinables: cada categoría marcada se serializa. */
  readonly categorias: CategoriasTecnicaAnestesica;
  readonly general: EstadoTecnicaGeneral;
  readonly regional: EstadoTecnicaRegional;
  readonly sedoanalgesia: EstadoTecnicaSedoanalgesia;
}

export function estadoInicialTecnicaAnestesica(): EstadoTecnicaAnestesica {
  return {
    categorias: { general: false, regional: false, sedoanalgesia: false },
    general: estadoInicialGeneral(),
    regional: estadoInicialRegional(),
    sedoanalgesia: estadoInicialSedoanalgesia(),
  };
}

/** `TFunction` de react-i18next es un tipo genérico incómodo de anotar acá — alcanza con `(clave: string) => string`, el único uso real (mismo criterio que `FormularioFemoPage.tsx`). */
export type Traducir = (clave: string) => string;

/** Casilla marcable (un solo recuadro en el papel, p. ej. Catéter): solo 'Sí' cuando está marcada. */
function marcada(t: Traducir, v: boolean): string {
  return v ? t('formulario.si') : '';
}

/** Par SI/NO impreso en el papel: 'Sí' / 'No' / nada si no se contestó (ver `SiNoValor`). */
function siNo(t: Traducir, v: unknown): string {
  return textoSiNo(v, { si: t('formulario.si'), no: t('formulario.no') });
}

function paresGeneral(t: Traducir, e: EstadoTecnicaGeneral): Par[] {
  return [
    { pregunta: t('formularios.f018a.bloqueF.general.sistema'), respuesta: e.sistema ? t(`formularios.f018a.bloqueF.general.sistemaOpciones.${e.sistema}`) : '' },
    { pregunta: t('formularios.f018a.bloqueF.general.aparato'), respuesta: e.aparato ? t(`formularios.f018a.bloqueF.general.aparatoOpciones.${e.aparato}`) : '' },
    { pregunta: t('formularios.f018a.bloqueF.general.manejoViaAerea'), respuesta: e.manejoViaAerea ? t(`formularios.f018a.bloqueF.general.manejoViaAereaOpciones.${e.manejoViaAerea}`) : '' },
    { pregunta: t('formularios.f018a.bloqueF.general.viaIntubacion'), respuesta: e.viaIntubacion ? t(`formularios.f018a.bloqueF.general.viaIntubacionOpciones.${e.viaIntubacion}`) : '' },
    { pregunta: t('formularios.f018a.bloqueF.general.visionIntubacion'), respuesta: e.visionIntubacion ? t(`formularios.f018a.bloqueF.general.visionIntubacionOpciones.${e.visionIntubacion}`) : '' },
    { pregunta: t('formularios.f018a.bloqueF.general.tipoTubo'), respuesta: e.tipoTubo ? t(`formularios.f018a.bloqueF.general.tipoTuboOpciones.${e.tipoTubo}`) : '' },
    { pregunta: t('formularios.f018a.bloqueF.general.diametroTubo'), respuesta: e.diametroTubo.trim() },
    { pregunta: t('formularios.f018a.bloqueF.general.balon'), respuesta: siNo(t, e.balon) },
    { pregunta: t('formularios.f018a.bloqueF.general.taponamiento'), respuesta: siNo(t, e.taponamiento) },
    { pregunta: t('formularios.f018a.bloqueF.general.equipoIntubacion'), respuesta: e.equipoIntubacion.trim() },
    { pregunta: t('formularios.f018a.bloqueF.general.cormack'), respuesta: e.cormack },
    { pregunta: t('formularios.f018a.bloqueF.general.intentosCormack'), respuesta: e.intentosCormack.trim() },
    { pregunta: t('formularios.f018a.bloqueF.general.induccion'), respuesta: e.induccion ? t(`formularios.f018a.bloqueF.general.induccionOpciones.${e.induccion}`) : '' },
    { pregunta: t('formularios.f018a.bloqueF.general.mantenimiento'), respuesta: e.mantenimiento ? t(`formularios.f018a.bloqueF.general.mantenimientoOpciones.${e.mantenimiento}`) : '' },
  ];
}

function paresRegional(t: Traducir, e: EstadoTecnicaRegional): Par[] {
  const comunes: Par[] = [
    { pregunta: t('formularios.f018a.bloqueF.regional.asepsiaCon'), respuesta: e.asepsiaCon.trim() },
    { pregunta: t('formularios.f018a.bloqueF.regional.habonCon'), respuesta: e.habonCon.trim() },
    { pregunta: t('formularios.f018a.bloqueF.regional.local'), respuesta: e.local ? t(`formularios.f018a.bloqueF.regional.localOpciones.${e.local}`) : '' },
    {
      pregunta: t('formularios.f018a.bloqueF.regional.subtipo'),
      respuesta: [e.troncular ? t('formularios.f018a.bloqueF.regional.subtipoOpciones.troncular') : '', e.neuroaxial ? t('formularios.f018a.bloqueF.regional.subtipoOpciones.neuroaxial') : ''].filter(Boolean).join(', '),
    },
  ];
  const troncular: Par[] =
    e.troncular
      ? [
          { pregunta: t('formularios.f018a.bloqueF.regional.troncular.bloqueoNervioPlexo'), respuesta: e.bloqueoNervioPlexo.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.troncular.intentos'), respuesta: e.intentosTroncular.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.troncular.anestesicoLocal'), respuesta: e.anestesicoLocalTroncular.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.troncular.coadyuvante'), respuesta: e.coadyuvanteTroncular.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.troncular.tipoAguja'), respuesta: e.tipoAgujaTroncular.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.troncular.equipo'), respuesta: e.equipoTroncular.trim() },
        ]
      : [];
  const neuroaxial: Par[] =
    e.neuroaxial
      ? [
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.abordaje'), respuesta: ABORDAJE_NEUROAXIAL_OPCIONES.filter((o) => e.abordajesNeuroaxial.includes(o)).map((o) => t(`formularios.f018a.bloqueF.regional.neuroaxial.abordajeOpciones.${o}`)).join(', ') },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.cateter'), respuesta: marcada(t, e.cateter) },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.tipoAguja'), respuesta: e.tipoAgujaNeuroaxial.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.numeroAguja'), respuesta: e.numeroAgujaNeuroaxial.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.intentos'), respuesta: e.intentosNeuroaxial.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.borbotaje'), respuesta: siNo(t, e.borbotaje) },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.acceso'), respuesta: ACCESO_NEUROAXIAL_OPCIONES.filter((o) => e.accesosNeuroaxial.includes(o)).map((o) => t(`formularios.f018a.bloqueF.regional.neuroaxial.accesoOpciones.${o}`)).join(', ') },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.sitioPuncion'), respuesta: e.sitioPuncion.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.dermatoma'), respuesta: e.dermatoma.trim() },
          { pregunta: t('formularios.f018a.bloqueF.regional.neuroaxial.posicion'), respuesta: e.posicionNeuroaxial.trim() },
        ]
      : [];
  return [...comunes, ...troncular, ...neuroaxial];
}

function paresSedoanalgesia(t: Traducir, e: EstadoTecnicaSedoanalgesia): Par[] {
  return [
    ...e.farmacos.map((f, i) => ({ pregunta: `${t('formularios.f018a.bloqueF.sedoanalgesia.farmaco')} #${i + 1}`, respuesta: f.texto.trim() })),
    { pregunta: t('formularios.f018a.bloqueF.sedoanalgesia.ramsay'), respuesta: e.ramsay },
  ];
}

/** Serializa CADA categoría marcada (son combinables); el estado de las categorías sin marcar (aunque haya quedado cargado) nunca se guarda. */
export function paresDeTecnicaAnestesia(t: Traducir, estado: EstadoTecnicaAnestesica): Par[] {
  const marcadas = CATEGORIAS_TECNICA.filter((c) => estado.categorias[c]);
  const pares: Par[] = [
    { pregunta: t('formularios.f018a.bloqueF.titulo'), respuesta: marcadas.map((c) => t(`formularios.f018a.bloqueF.categorias.${c}`)).join(', ') },
    ...(estado.categorias.general ? paresGeneral(t, estado.general) : []),
    ...(estado.categorias.regional ? paresRegional(t, estado.regional) : []),
    ...(estado.categorias.sedoanalgesia ? paresSedoanalgesia(t, estado.sedoanalgesia) : []),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
